/**
 * Normalizes a phone number for search comparison.
 * Removes spaces and special characters, and extracts the searchable pattern.
 * 
 * Rules:
 * - "098 123 123" should match "+598 98 123 123" (or any international format containing "98123123")
 * - "+598 98 123 123" should match "098 123 123" (or any format containing "98123123")
 * - Works generically for any country code
 * 
 * @param phone - The phone number to normalize
 * @returns The normalized phone number pattern for searching (digits only, no spaces)
 * 
 * @example
 * normalizePhoneForSearch("098 123 123") // "98123123"
 * normalizePhoneForSearch("+598 98 123 123") // "59898123123"
 * normalizePhoneForSearch("+59898123123") // "59898123123"
 * normalizePhoneForSearch("098123123") // "98123123"
 */
export function normalizePhoneForSearch(phone: string): string {
  if (!phone) return "";
  
  // Remove all spaces, dashes, parentheses, and other non-digit characters except +
  let normalized = phone.replace(/[\s\-\(\)]/g, "");
  
  // If empty after cleaning, return empty
  if (!normalized) return "";
  
  // If it starts with +, remove it and return digits only
  if (normalized.startsWith("+")) {
    return normalized.substring(1);
  }
  
  // If it starts with one or more zeros, remove them (e.g., 098 -> 98)
  // This handles local format like "098 123 123"
  normalized = normalized.replace(/^0+/, "");
  
  return normalized;
}

/**
 * Checks if a phone number matches a search query.
 * Handles both local format (098) and international format (+598) generically.
 * 
 * @param phone - The phone number to check
 * @param searchQuery - The search query
 * @returns True if the phone matches the search query
 * 
 * @example
 * phoneMatchesSearch("+59898123123", "098 123 123") // true
 * phoneMatchesSearch("+59898123123", "+598 98 123 123") // true
 * phoneMatchesSearch("+5498123123", "098 123 123") // true (works for any country)
 */
export function phoneMatchesSearch(phone: string, searchQuery: string): boolean {
  if (!phone || !searchQuery) return false;
  
  const normalizedPhone = normalizePhoneForSearch(phone);
  const normalizedQuery = normalizePhoneForSearch(searchQuery);
  
  if (!normalizedPhone || !normalizedQuery) return false;
  
  // Check if the normalized phone contains the normalized query
  // This allows partial matches (e.g., "98123" matches "+59898123123")
  return normalizedPhone.includes(normalizedQuery) || normalizedQuery.includes(normalizedPhone);
}
